/* eslint-disable complexity */
import Cookies from 'js-cookie';
import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Banner} from './Banner/Banner';
import {BannerModal} from './Modal/Modal';
import {EmbeddedComponent} from './EmbeddedComponent/EmbeddedComponent';
import {getBrand, RATING_TEXT, appBannerCtaLinks, appModalCtaLinks, RATINGS, STARS} from './utils/bannerHelpers';
import {getExperimentValue, renderExperiments, textExperiments} from './utils/experiment';
import {I18n, readTransalationsBundles} from './utils/translation';
import {initBranch} from './utils/branch';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {triggerEdapEvent} from './utils/triggerEdapEvent';

function AppBanner({locale, mobileOS, page, position}) {
    const renderNothing = () => {
        return (<Fragment />);
    };

    const fullLocale = locale ? locale : 'en_US_VRBO';
    locale = locale ? locale.match(/^.{2}_.{2}/)[0] : 'en_US';
    mobileOS = mobileOS || 'ios';

    const appBrandName = getBrand(fullLocale, locale);
    const travelerApp = appBrandName.toLowerCase();
    const getCtaLinks = (displayType) => {
        switch (displayType) {
            case 'banner' :
                return appBannerCtaLinks[travelerApp][page];
            case 'modal' :
                return appModalCtaLinks[travelerApp][page];
            case 'embedded':
                return appModalCtaLinks[travelerApp][`${page}-${position}`];
            default :
                return '';
        }
    };
    const bannerCtaLink = (travelerApp && page) ? getCtaLinks('banner') : null;
    const embeddedCtaLink = (travelerApp && page) ? getCtaLinks('embedded') : null;
    const modalCtaLink = (travelerApp && page) ? getCtaLinks('modal') : null;

    const useCanonicalPath = (page && page.toLowerCase() === 'serp') ? true : false;

    let props = {
        travelerApp,
        useCanonicalPath
    };

    const [textVariant, setTextVariant] = useState(0);
    const [labels, setLabels] = useState({});
    const [branchInfo, setBranchInfo] = useState({});
    const [componentType, setComponentType] = useState({});

    const context = useContext(TrackingContext);

    const getLabels = async () => {
        await I18n.load(locale);
        const labelObj = page ? readTransalationsBundles(page, {brand: appBrandName}) : {};
        labelObj.ratings = labelObj.ratings || RATING_TEXT;
        const mobileOsRating = RATINGS[`${mobileOS}`];
        labelObj.rating = `${mobileOsRating} ${labelObj.ratings}`;
        labelObj.stars = STARS[`${mobileOS}`];
        setLabels(labelObj);
    };
    const euSiteCookie = Cookies.get('eu-site');
    const gdprCookie = Cookies.get('ha-cookie-settings'); // 'ha-cookie-settings' is a GDPR cookie used to set the consent for PII by EU user.

    const hasGdprClearance = () => {
        return !(euSiteCookie === '1' && gdprCookie === 'functional:1'); // 'functional:0' is the consent for PII
    };

    const isGDPRBannerHidden = () => {
        return (!euSiteCookie || euSiteCookie === '0' || gdprCookie);
    };

    const getExperimentVariant = () => {
        const displayExpName = renderExperiments[page];
        return (displayExpName ? getExperimentValue(displayExpName) : -1);
    };

    const getComponentType = () => {
        const displayExpVariant = getExperimentVariant();
        let componentTypeInfo = 'others';
        switch (page) {
            case 'mytrips':
                if (displayExpVariant === -1 || displayExpVariant === 1) {
                    componentTypeInfo = 'banner';
                } else if (displayExpVariant === 2) {
                    componentTypeInfo = 'modal';
                }
                break;
            case 'confirmation':
                if ((displayExpVariant === 1 && !position) || displayExpVariant === -1) {
                    componentTypeInfo = 'modal';
                } else if ((displayExpVariant === 2 && position === 'above-share-trip') || (displayExpVariant === 3 && position === 'below-share-trip')) {
                    componentTypeInfo = 'embedded';
                }
                break;
            case 'serp':
                if (displayExpVariant === 1 || displayExpVariant === -1) {
                    componentTypeInfo = 'banner';
                }
                break;
            case 'homepage':
                if (displayExpVariant === -1 || displayExpVariant === 1) {
                    componentTypeInfo = 'banner';
                } else if (displayExpVariant === 2) {
                    componentTypeInfo = 'modal';
                }
                break;
            case 'inbox':
                if (displayExpVariant === 1 || displayExpVariant === -1) {
                    componentTypeInfo = 'banner';
                }
                break;
            default:
                componentTypeInfo = 'others';
        }
        return componentTypeInfo;
    };

    const setBranchAndComponentTypeInfo = () => {
        const componentTypeInfo = getComponentType();
        setComponentType(componentTypeInfo);
        const hasAppExpVariant = getExperimentValue('HA_NATIVE_ADOPTION_BANNER_USE_HAS_APP');
        branchInfo.branchInitializationRequired = (componentTypeInfo !== 'others' && hasAppExpVariant === 1 && hasGdprClearance());
        setBranchInfo(branchInfo);
    };

    const setComponentTypeAndInitilizeBranchSDK = () => {
        setBranchAndComponentTypeInfo();
        if (branchInfo.branchInitializationRequired && !branchInfo.hasApp) {
            try {
                initBranch((err, data) => {
                    const hasApp = (data && data.has_app) ? 'true' : 'false';
                    branchInfo.hasApp = hasApp;
                    setBranchInfo({...branchInfo, branchInfo});
                }, false);
            } catch (err) {
                branchInfo.hasApp = 'false';
                setBranchInfo({...branchInfo, branchInfo});
            }
        }
    };

    useEffect(() => {
        const textExpName = textExperiments[page];
        const textExpVariant = getExperimentValue(textExpName);
        setTextVariant(textExpVariant);
    });

    useEffect(() => {
        setComponentTypeAndInitilizeBranchSDK();
        getLabels();
    }, []);

    const renderBanner = () => {
        return (<Banner ctaLink={bannerCtaLink} {...labels} {...props}/>);
    };

    const renderModal = () => {
        return (<BannerModal ctaLink={modalCtaLink} {...labels} {...props}/>);
    };

    const renderEmbeddedComponent = () => {
        return (<EmbeddedComponent ctaLink={embeddedCtaLink} {...labels} {...props}/>);
    };

    const renderMyTrips = () => {
        switch (componentType) {
            case 'banner':
                return renderBanner();
            case 'modal':
                return renderModal();
            default:
                return renderNothing();
        }
    };
    const renderInbox = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderConfirmation = () => {
        if (componentType === 'modal') {
            return renderModal();
        } else if (componentType === 'embedded') {
            return renderEmbeddedComponent();
        }
        return renderNothing();
    };
    const renderSerp = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderHomePage = () => {
        switch (componentType) {
            case 'banner':
                if (textVariant === 1) {
                    labels.description = labels.description_v1;
                    labels.title = labels.title_v1;
                } else if (textVariant === 2) {
                    labels.description = 'empty_description';
                    labels.title = labels.title_v1;
                }
                return renderBanner();
            case 'modal':
                labels.description = labels.modal_description;
                labels.title = labels.modal_title;
                labels.buttonLabel = labels.modal_buttonLabel;
                return renderModal();
            default:
                return renderNothing();
        }
    };
    const renderDefault = () => {
        return renderBanner();
    };

    const triggerHasAppEdapEvent = () => {
        if (branchInfo.hasApp === 'true') {
            triggerEdapEvent(context, 'hidden', componentType);
        }
    };

    const mobAppInstalled = () => {
        return branchInfo.branchInitializationRequired && (!branchInfo.hasApp || branchInfo.hasApp === 'true');
    };

    const appBanner = () => {
        if (mobAppInstalled()) {
            triggerHasAppEdapEvent();
            return renderNothing();
        }
        if (!isGDPRBannerHidden() && componentType !== 'embedded') {
            return renderNothing();
        }
        switch (page) {
            case 'mytrips':
                return renderMyTrips();
            case 'confirmation':
                return renderConfirmation();
            case 'serp':
                return renderSerp();
            case 'homepage':
                return renderHomePage();
            case 'inbox':
                return renderInbox();
            default: return renderDefault();
        }
    };
    return appBanner();
}

export default AppBanner;
